import React from 'react';
import classNames from 'classnames';

// Libs
import SectionUtil from 'Utils/content/section-util';

// Views
import NoItems from './no-items';

// Components
import Item, { ITEM_DISPLAY_TYPES } from '../../components/item';
import { CondensedItemModifiersType } from '../../components/item/item-condensed';
import InfeedAdCard from 'Webapp/shared/app/components/ads/infeed-ad-card';
import {
  getInfeedAdInterval,
  isInfeedAdEnabled,
} from 'Webapp/shared/app/components/ads/ads-config';

import connector from 'Utils/connector';
import connectResponsive, {
  ConnectResponsiveProps,
} from 'Webapp/shared/app/connectors/connectResponsive';
import connectIsArticleRoute, {
  ConnectIsArticleRouteProps,
} from 'Webapp/shared/app/connectors/connectIsArticleRoute';
import connectCurrentUser, {
  ConnectCurrentUserProps,
} from 'Webapp/shared/app/connectors/connectCurrentUser';
import connectFeatureFlags, {
  ConnectFeatureFlagsProps,
} from 'Webapp/shared/app/connectors/connectFeatureFlags';

interface ItemListPassedProps {
  className: string;
  section: Flipboard.Section;
  items: Array<Flipboard.Item>;
  isLargeScreen: boolean;
  preventExcerpt?: boolean;
  truncateTitleLength?: number;
  displayType?: string;
  condensedItemModifiers?: Array<CondensedItemModifiersType>;
  styleModifier?: typeof ITEM_LIST_STYLE_MODIFIERS;
  limit?: number | null;
  // Opt-in (topic/magazine grids): insert an ad card as an EXTRA tile every
  // N story tiles (N from the WEB_ADS_INFEED_INTERVAL_* flags, default 9).
  // The cards themselves are flag-gated (WEB_ADS + WEB_ADS_INFEED).
  showInfeedAds?: boolean;
}

type ItemListProps = ConnectResponsiveProps &
  ConnectCurrentUserProps &
  ConnectIsArticleRouteProps &
  ConnectFeatureFlagsProps &
  ItemListPassedProps;

const ItemList: React.FC<ItemListProps> = ({
  className,
  section,
  items,
  isLargeScreen,
  preventExcerpt = false,
  truncateTitleLength = 120,
  displayType = ITEM_DISPLAY_TYPES.CARD,
  isArticleRoute,
  condensedItemModifiers,
  styleModifier = ITEM_LIST_STYLE_MODIFIERS.DEFAULT,
  limit,
  currentUserData,
  showInfeedAds = false,
  featureFlags,
}) => {
  if (!SectionUtil.hasItems(section, items)) {
    return <NoItems />;
  }

  let itemsToRender = SectionUtil.itemsToRender(
    section,
    items,
    currentUserData,
  );
  if (limit) {
    itemsToRender = itemsToRender.slice(0, limit);
  }

  const showExcerpt = isArticleRoute || (!preventExcerpt && isLargeScreen);

  const listClassName = classNames(className, 'item-list', {
    'item-list--grid': styleModifier === ITEM_LIST_STYLE_MODIFIERS.GRID,
    'item-list--related-context':
      styleModifier === ITEM_LIST_STYLE_MODIFIERS.RELATED,
  });

  const shouldInsertInfeedAds =
    showInfeedAds &&
    styleModifier === ITEM_LIST_STYLE_MODIFIERS.GRID &&
    isInfeedAdEnabled(featureFlags);
  const infeedAdInterval = getInfeedAdInterval(featureFlags);

  return (
    <ul className={listClassName}>
      {itemsToRender.reduce((listItems, item, index) => {
        listItems.push(
          <li className="item-list__item" key={`${index}-${item.id}`}>
            <Item
              item={item}
              section={section}
              shownInList
              truncateTitleLength={truncateTitleLength}
              truncateExcerptLength={200}
              displayType={displayType}
              condensedItemModifiers={condensedItemModifiers}
              showExcerpt={showExcerpt}
            />
          </li>,
        );
        // Insert the ad card as an extra tile (content is never replaced)
        if (shouldInsertInfeedAds && (index + 1) % infeedAdInterval === 0) {
          listItems.push(
            <li
              className="item-list__item item-list__item--ad"
              key={`infeed-ad-${index + 1}`}
            >
              <InfeedAdCard adjacentItems={[item, itemsToRender[index + 1]]} />
            </li>,
          );
        }
        return listItems;
      }, [] as Array<React.ReactNode>)}
    </ul>
  );
};

export const ITEM_LIST_STYLE_MODIFIERS = {
  DEFAULT: 'default',
  GRID: 'grid',
  RELATED: 'related',
};

export default connector<ItemListProps>(
  connectIsArticleRoute,
  connectResponsive,
  connectCurrentUser,
  connectFeatureFlags,
)(ItemList);
